import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  Paper,
  Snackbar,
  Alert,
  IconButton,
  Slide,
  useMediaQuery,
  useTheme,
  InputAdornment
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";

import AddIcon from "@mui/icons-material/Add";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ScaleIcon from "@mui/icons-material/Scale";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import {
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  Area,
  Scatter,
  CartesianGrid
} from "recharts";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import useThemeColor from "../../utils/useTheme";
import {
  fetchWeightData,
  postWeightData,
  deleteWeightEntry,
  fetchAvgData
} from "../../api/userApi";
import { useSelector } from "react-redux";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(utc);

const kgToLb = (kg) => kg * 2.2046226218;

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const CustomPickersDay = ({ completedDates, ...props }) => {
  const dayString = dayjs(props.day).format("YYYY-MM-DD");
  const isCompleted = completedDates?.includes(dayString);
  const styles = useThemeColor();

  return (
    <Box sx={{ position: "relative", display: "inline-block" }}>
      <PickersDay
        {...props}
        sx={{
          ...props.sx,
          borderRadius: isCompleted ? "50% !important" : "4px",
          border: isCompleted
            ? `1px solid ${styles.primaryColor} !important`
            : "none",
          "&.Mui-selected": {
            backgroundColor: `${styles.primaryColor} !important`
          }
        }}
      />
    </Box>
  );
};

const WeightTrackerContent = ({ userId }) => {
  const styles = useThemeColor();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery("(min-width: 600px) and (max-width: 900px)");

  const [unit, setUnit] = useState(localStorage.getItem("weightUnit") || "lb");
  const savedFilter = JSON.parse(localStorage.getItem("weightDateFilter"));

  const [dateRange, setDateRange] = useState(savedFilter?.range || "14");

  const [startDate, setStartDate] = useState(
    savedFilter?.start || dayjs.utc().subtract(13, "day").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(
    savedFilter?.end || dayjs.utc().format("YYYY-MM-DD")
  );
  const [openCustomDateDialog, setOpenCustomDateDialog] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(
    savedFilter?.customStart ||
      dayjs.utc().subtract(30, "day").format("YYYY-MM-DD")
  );
  const [customEndDate, setCustomEndDate] = useState(
    savedFilter?.customEnd || dayjs.utc().format("YYYY-MM-DD")
  );
  const [globalAvg, setGlobalAvg] = useState(null);
  const [dateError, setDateError] = useState("");

  const [dailyPoints, setDailyPoints] = useState([]);
  const [allCompleteDates, setAllCompleteDates] = useState([]);
  const [loading, setLoading] = useState(false);

  const [openAdd, setOpenAdd] = useState(false);

  const [formWeight, setFormWeight] = useState("");
  const [formUnit, setFormUnit] = useState("lb");
  const [formDate, setFormDate] = useState(dayjs.utc().format("YYYY-MM-DD"));

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const openSnack = (msg, severity = "success") => {
    setSnackbar({ open: true, message: msg, severity });
  };

  // Load Data
  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    fetchWeightData({ userId: userId, start: startDate, end: endDate })
      .then((res) => {
        setDailyPoints(res.data.dailyPoints || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [startDate, endDate, userId]);

  useEffect(() => {
    if (!userId) return;
    fetchAvgData({ userId: userId }).then((res) => {
      if (res.avgKg) {
        setGlobalAvg(unit === "kg" ? res.avgKg : kgToLb(res.avgKg));
        setAllCompleteDates(res.allCompleteDates || []);
      }
    });
  }, [unit, userId]);

  useEffect(() => {
    localStorage.setItem(
      "weightDateFilter",
      JSON.stringify({
        range: dateRange,
        start: startDate,
        end: endDate,
        customStart: customStartDate,
        customEnd: customEndDate
      })
    );
  }, [dateRange, startDate, endDate, customStartDate, customEndDate]);

  const handleDateRangeChange = (value) => {
    setDateRange(value);

    if (value === "custom") {
      setOpenCustomDateDialog(true);
    } else {
      const days = parseInt(value);
      const newStart = dayjs
        .utc()
        .subtract(days - 1, "day")
        .format("YYYY-MM-DD");
      const newEnd = dayjs.utc().format("YYYY-MM-DD");

      setStartDate(newStart);
      setEndDate(newEnd);

      localStorage.setItem(
        "weightDateFilter",
        JSON.stringify({
          range: value,
          start: newStart,
          end: newEnd,
          customStart: customStartDate,
          customEnd: customEndDate
        })
      );
    }
  };

  const handleCustomDateApply = () => {
    if (
      dayjs(customEndDate).isBefore(customStartDate) ||
      dayjs(customEndDate).isSame(customStartDate)
    ) {
      setDateError("End date must be greater than start date");
      return;
    }

    setDateError("");
    setStartDate(customStartDate);
    setEndDate(customEndDate);
    setDateRange("custom");

    localStorage.setItem(
      "weightDateFilter",
      JSON.stringify({
        range: "custom",
        start: customStartDate,
        end: customEndDate,
        customStart: customStartDate,
        customEnd: customEndDate
      })
    );

    setOpenCustomDateDialog(false);
  };

  const chartData = useMemo(() => {
    if (!dailyPoints?.length) return [];

    const mapped = dailyPoints.map((p) => ({
      date: p.date,
      weight: unit === "kg" ? p.weightKg : kgToLb(p.weightKg),
      avg: p.avgKg ? (unit === "kg" ? p.avgKg : kgToLb(p.avgKg)) : null
    }));

    const firstDate = dayjs
      .utc(mapped[0].date)
      .subtract(1, "day")
      .format("YYYY-MM-DD");
    const lastDate = dayjs
      .utc(mapped[mapped.length - 1].date)
      .add(1, "day")
      .format("YYYY-MM-DD");

    return [
      { date: firstDate, weight: null, avg: null },
      ...mapped,
      { date: lastDate, weight: null, avg: null }
    ];
  }, [dailyPoints, unit]);

  const todayWeight = useMemo(() => {
    const todayStr = dayjs.utc().format("YYYY-MM-DD");
    const found = chartData.find((d) => d.date === todayStr);
    return found?.weight?.toFixed(1) ?? "--";
  }, [chartData]);

  const todayRollingAvg = useMemo(() => {
    const todayStr = dayjs.utc().format("YYYY-MM-DD");
    const found = chartData.find((d) => d.date === todayStr);

    return found?.avg != null ? found.avg.toFixed(1) : "--";
  }, [chartData]);

  const mostRecentData = useMemo(() => {
    if (!chartData.length) return null;

    const validEntries = chartData.filter((d) => d.weight != null);
    if (!validEntries.length) return null;

    const mostRecent = validEntries[validEntries.length - 1];

    return {
      date: mostRecent.date,
      weight: mostRecent.weight.toFixed(1),
      avg: mostRecent.avg != null ? mostRecent.avg.toFixed(1) : "--"
    };
  }, [chartData]);

  const hasTodayData = todayWeight !== "--";

  const stats = useMemo(() => {
    if (!chartData.length) return null;

    const weights = chartData.map((d) => d.weight).filter(Boolean);
    if (!weights.length) return null;

    const start = weights[0];
    const current = weights[weights.length - 1];
    const change = current - start;
    const changePercent = ((change / start) * 100).toFixed(1);

    return {
      current,
      change: change.toFixed(1),
      changePercent,
      avg: (weights.reduce((a, b) => a + b, 0) / weights.length).toFixed(1),
      min: Math.min(...weights).toFixed(1),
      max: Math.max(...weights).toFixed(1),
      isGain: change >= 0,
      entries: weights.length
    };
  }, [chartData]);

  const handleAddEntry = async () => {
    if (!formWeight || !formDate) {
      openSnack("Please enter weight and date", "error");
      return;
    }

    try {
      const timestamp = dayjs.utc(formDate).startOf("day").toISOString();
      const res = await postWeightData({
        userId: userId,
        timestamp,
        weight: Number(formWeight),
        unit: formUnit
      });

      if (res.message === "updated") {
        openSnack("Weight updated for this date!");
      } else {
        openSnack("Weight entry created!");
      }

      setOpenAdd(false);
      setFormWeight("");
      setFormDate(dayjs.utc().format("YYYY-MM-DD"));
      const updatedDates = await fetchAvgData({ userId: userId });
      const fresh = await fetchWeightData({
        userId: userId,
        start: startDate,
        end: endDate
      });
      setDailyPoints(fresh.data.dailyPoints || []);
      setAllCompleteDates(updatedDates.allCompleteDates || []);
    } catch (err) {
      console.log("Error adding entry", err);
      console.error(err);
      openSnack("Error adding entry", "error");
    }
  };

  const yDomain = useMemo(() => {
    if (chartData.length === 0) {
      return unit === "kg" ? [0, 100] : [0, 220];
    }

    const values = chartData
      .flatMap((d) => [d.weight, d.avg])
      .filter((v) => v !== null && v !== undefined && !isNaN(v));

    if (values.length === 0) {
      return unit === "kg" ? [0, 100] : [0, 220];
    }

    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min;

    const padPercentage = 0.2;
    const pad = Math.max(unit === "kg" ? 3 : 8, range * padPercentage);

    const roundToNice = (val, direction) => {
      let step;
      if (unit === "kg") {
        step = range > 50 ? 10 : range > 20 ? 5 : 2;
      } else {
        step = range > 100 ? 20 : range > 50 ? 10 : 5;
      }

      if (direction === "down") {
        return Math.floor(val / step) * step;
      }
      return Math.ceil(val / step) * step;
    };

    return [roundToNice(min - pad, "down"), roundToNice(max + pad, "up")];
  }, [chartData, unit]);

  const yAxisTicks = useMemo(() => {
    const [min, max] = yDomain;
    const range = max - min;

    let step;
    if (unit === "kg") {
      if (range <= 10) step = 1;
      else if (range <= 20) step = 2;
      else if (range <= 50) step = 5;
      else step = 10;
    } else {
      if (range <= 20) step = 2;
      else if (range <= 50) step = 5;
      else if (range <= 100) step = 10;
      else step = 20;
    }

    const ticks = [];
    for (let i = min; i <= max; i += step) {
      ticks.push(i);
    }
    return ticks;
  }, [yDomain, unit]);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          sx={{
            background: styles.modBackgroundBlocks,
            border: `1px solid ${styles.lightBlue}`,
            borderRadius: 2.5,
            p: 2,
            boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
            minWidth: 140
          }}
        >
          <Typography
            sx={{
              fontSize: "11px",
              color: styles.modLabel,
              fontWeight: 500,
              mb: 1,
              textTransform: "uppercase",
              letterSpacing: 0.5
            }}
          >
            {dayjs.utc(data.date).format("ddd, MMM D")}
          </Typography>

          {data.weight && (
            <Box display="flex" alignItems="baseline" gap={0.5} mb={0.5}>
              <Typography
                sx={{
                  fontSize: "22px",
                  fontWeight: 700,
                  color: styles.color,
                  lineHeight: 1
                }}
              >
                {data.weight.toFixed(1)}
              </Typography>
              <Typography sx={{ fontSize: "12px", color: styles.modLabel }}>
                {unit}
              </Typography>
            </Box>
          )}

          {data.avg && (
            <Box
              sx={{
                mt: 1,
                pt: 1,
                borderTop: `1px solid ${styles.lightBlue}`
              }}
            >
              <Typography
                sx={{
                  fontSize: "10px",
                  color: styles.modLabel,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  mb: 0.25
                }}
              >
                Rolling Avg
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  color: styles.primaryColor,
                  fontWeight: 600
                }}
              >
                {data.avg.toFixed(1)} {unit}
              </Typography>
            </Box>
          )}
        </Box>
      );
    }
    return null;
  };

  const CustomScatterPoint = (props) => {
    const { cx, cy, payload } = props;
    if (!cx || !cy || payload.weight === null) return null;

    const dotSize = isMobile ? 5 : 7;
    const glowSize = isMobile ? 10 : 14;

    return (
      <g>
        <circle
          cx={cx}
          cy={cy}
          r={glowSize}
          fill={styles.primaryColor}
          opacity={0.15}
        />
        <circle
          cx={cx}
          cy={cy}
          r={dotSize}
          fill={styles.primaryColor}
          stroke="#fff"
          strokeWidth={isMobile ? 1.5 : 2}
          style={{
            cursor: "pointer",
            filter: `drop-shadow(0 2px 4px rgba(54, 138, 208, 0.4))`
          }}
        />
      </g>
    );
  };

  const dateRangeOptions = [
    { value: "14", label: "14D" },
    { value: "30", label: "30D" },
    { value: "custom", label: "Custom" }
  ];

  const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      background: styles.background,
      borderRadius: 2.5,
      transition: "all 0.2s ease",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: styles.primaryColor,
        borderWidth: 1.5
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: styles.primaryColor
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: styles.primaryColor,
        borderWidth: 2
      }
    },
    "& input": {
      color: styles.color,
      fontSize: "16px",
      fontWeight: 500,
      "&::placeholder": {
        color: styles.modLabel,
        opacity: 0.7
      }
    }
  };

  return (
    <Box
      sx={{
        background: styles.background,
        pb: isMobile ? 8 : 2,
        px: isMobile ? 2 : 3,
        pt: 2,
        minWidth: 700
      }}
    >
      {/* Current Weight Card */}
      {stats && (
        <Box
          sx={{
            background: styles.modBackgroundBlocks,
            borderRadius: 3,
            p: isMobile ? 2.5 : 3,
            mb: 2.5
          }}
        >
          <Box
            display="flex"
            flexDirection={"row"}
            alignItems={isMobile ? "flex-start" : "center"}
            justifyContent="space-between"
            gap={2}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: styles.modLabel,
                  letterSpacing: 1.5,
                  textTransform: "uppercase",
                  fontWeight: 600,
                  mb: 0.5
                }}
              >
                {hasTodayData ? "Current 7-Day Average" : "Most Recent Average"}
              </Typography>
              <Box display="flex" alignItems="baseline" gap={1}>
                <Typography
                  sx={{
                    fontSize: isMobile ? "34px" : "44px",
                    fontWeight: 700,
                    color: styles.color,
                    lineHeight: 1,
                    letterSpacing: "-1px"
                  }}
                >
                  {hasTodayData ? todayRollingAvg : mostRecentData?.avg || "--"}
                </Typography>

                <Typography
                  sx={{
                    fontSize: isMobile ? "18px" : "22px",
                    fontWeight: 500,
                    color: styles.modLabel
                  }}
                >
                  {(
                    hasTodayData
                      ? !todayRollingAvg?.includes("--")
                      : mostRecentData?.avg !== "--"
                  )
                    ? unit
                    : ""}
                </Typography>
              </Box>
              <Box mt={2}>
                <Typography
                  sx={{
                    fontSize: "11px",
                    color: styles.modLabel,
                    letterSpacing: 1.5,
                    textTransform: "uppercase",
                    fontWeight: 600,
                    mb: 0.5
                  }}
                >
                  {hasTodayData
                    ? "Today's Weight"
                    : mostRecentData?.date
                    ? `${dayjs
                        .utc(mostRecentData.date)
                        .format("D MMM YYYY")} Weight`
                    : "Today's Weight"}
                </Typography>

                <Box display="flex" alignItems="baseline" gap={1}>
                  <Typography
                    sx={{
                      fontSize: isMobile ? "34px" : "44px",
                      fontWeight: 700,
                      color: styles.color,
                      lineHeight: 1,
                      letterSpacing: "-1px"
                    }}
                  >
                    {hasTodayData
                      ? todayWeight
                      : mostRecentData?.weight || "--"}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: isMobile ? "18px" : "22px",
                      fontWeight: 500,
                      color: styles.modLabel
                    }}
                  >
                    {(
                      hasTodayData
                        ? !todayWeight?.includes("--")
                        : mostRecentData?.weight !== "--"
                    )
                      ? unit
                      : ""}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Unit Toggle */}
            <ToggleButtonGroup
              value={unit}
              exclusive
              onChange={(e, v) => {
                if (v) {
                  setUnit(v);
                  localStorage.setItem("weightUnit", v);
                }
              }}
              size="small"
              sx={{
                background: styles.background,
                borderRadius: 2.5,
                p: 0.5,
                "& .MuiToggleButton-root": {
                  border: "none",
                  color: styles.modLabel,
                  px: 2.5,
                  py: 0.75,
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  borderRadius: "8px !important",
                  transition: "all 0.2s ease",
                  "&.Mui-selected": {
                    backgroundColor: styles.primaryColor,
                    color: "#fff",
                    boxShadow: "0 2px 8px rgba(54,138,208,0.3)"
                  },
                  "&:hover": {
                    backgroundColor: styles.primaryColor,
                    color: "#fff"
                  }
                }
              }}
            >
              <ToggleButton value="lb">LB</ToggleButton>
              <ToggleButton value="kg">KG</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
      )}

      {/* Chart Section */}
      <Box
        sx={{
          background: styles.modBackgroundBlocks,
          borderRadius: 3,
          overflow: "hidden",
          mb: 2
        }}
      >
        <Box
          sx={{
            p: isMobile ? "16px 16px 12px" : "20px 24px 16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1.5
          }}
        >
          <Typography
            sx={{
              fontSize: isMobile ? "14px" : "16px",
              fontWeight: 600,
              color: styles.color
            }}
          >
            Weight Progress
          </Typography>

          <Box
            sx={{
              display: "flex",
              background: styles.background,
              borderRadius: 2,
              p: 0.5,
              gap: 0.25
            }}
          >
            {dateRangeOptions.map((opt) => (
              <Box
                key={opt.value}
                onClick={() => handleDateRangeChange(opt.value)}
                sx={{
                  px: isMobile ? 1.5 : 2,
                  py: 0.75,
                  borderRadius: 1.5,
                  cursor: "pointer",
                  background:
                    dateRange === opt.value
                      ? styles.primaryColor
                      : "transparent",
                  color: dateRange === opt.value ? "#fff" : styles.modLabel,
                  fontWeight: dateRange === opt.value ? 600 : 500,
                  fontSize: isMobile ? "12px" : "13px",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    background:
                      dateRange === opt.value
                        ? styles.primaryColor
                        : `${styles.primaryColor}20`
                  }
                }}
              >
                {opt.label}
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ p: isMobile ? "12px 4px 16px" : "20px 16px 24px" }}>
          {loading ? (
            <Box
              height={isMobile ? 280 : 340}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              gap={2}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  border: `3px solid ${styles.lightBlue}`,
                  borderTop: `3px solid ${styles.primaryColor}`,
                  borderRadius: "50%",
                  animation: "spin 0.8s linear infinite",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" }
                  }
                }}
              />
              <Typography sx={{ color: styles.modLabel, fontSize: "14px" }}>
                Loading data...
              </Typography>
            </Box>
          ) : chartData.length === 0 ? (
            <Box
              height={isMobile ? 280 : 340}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              gap={2}
            >
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  background: `${styles.primaryColor}15`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <ScaleIcon sx={{ fontSize: 36, color: styles.primaryColor }} />
              </Box>
              <Typography
                sx={{
                  color: styles.color,
                  fontSize: "16px",
                  fontWeight: 500
                }}
              >
                No data for selected range
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                userSelect: "none",
                "& .recharts-wrapper": {
                  outline: "none !important",
                  cursor: "default !important",
                  "& g": {
                    userSelect: "none",
                    outline: "none !important"
                  }
                },
                "& .recharts-surface": {
                  outline: "none !important"
                },
                "& .recharts-area": {
                  pointerEvents: "none"
                },
                "& .recharts-cartesian-grid": {
                  pointerEvents: "none"
                },
                "& .recharts-xAxis, & .recharts-yAxis": {
                  pointerEvents: "none"
                },
                "& .recharts-area-curve": {
                  pointerEvents: "none"
                },
                "& .recharts-area-area": {
                  pointerEvents: "none"
                },
                "& .recharts-scatter-symbol": {
                  pointerEvents: "auto",
                  cursor: "pointer"
                }
              }}
            >
              <ResponsiveContainer width="100%" height={isMobile ? 280 : 340}>
                <ComposedChart
                  data={chartData}
                  margin={{
                    top: 20,
                    right: isMobile ? 20 : 30,
                    left: isMobile ? 5 : 10,
                    bottom: isMobile ? 45 : 15
                  }}
                >
                  <defs>
                    <linearGradient
                      id="areaGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="0%"
                        stopColor={styles.primaryColor}
                        stopOpacity={0.25}
                      />
                      <stop
                        offset="100%"
                        stopColor={styles.primaryColor}
                        stopOpacity={0.02}
                      />
                    </linearGradient>
                  </defs>

                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={styles.lightBlue}
                    opacity={0.3}
                    vertical={false}
                  />

                  <XAxis
                    dataKey="date"
                    tickFormatter={(d) => dayjs.utc(d).format("M/D")}
                    axisLine={false}
                    tickLine={false}
                    tick={{
                      fill: styles.modLabel,
                      fontSize: isMobile ? 9 : 10
                    }}
                    dy={10}
                    interval={0}
                    minTickGap={isMobile ? 20 : 25}
                    angle={isMobile ? -45 : 0}
                    textAnchor={isMobile ? "end" : "middle"}
                    height={isMobile ? 50 : 40}
                  />

                  <YAxis
                    domain={yDomain}
                    ticks={yAxisTicks}
                    axisLine={false}
                    tickLine={false}
                    tick={{
                      fill: styles.modLabel,
                      fontSize: isMobile ? 10 : 11
                    }}
                    dx={-5}
                    width={isMobile ? 50 : 60}
                    tickFormatter={(value) => `${Math.round(value)}`}
                  />

                  <Tooltip content={<CustomTooltip />} />

                  <Area
                    type="monotone"
                    dataKey="avg"
                    stroke={styles.primaryColor}
                    strokeWidth={isMobile ? 2 : 2.5}
                    fill="url(#areaGradient)"
                    dot={false}
                    activeDot={false}
                    isAnimationActive={true}
                    animationDuration={1000}
                    animationEasing="ease-out"
                  />

                  <Scatter
                    dataKey="weight"
                    shape={<CustomScatterPoint />}
                    isAnimationActive={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          )}
        </Box>

        {chartData.length > 0 && (
          <Box
            sx={{
              px: isMobile ? 2 : 3,
              py: 1.5,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: isMobile ? 2.5 : 4,
              flexWrap: "wrap"
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: isMobile ? 8 : 10,
                  height: isMobile ? 8 : 10,
                  borderRadius: "50%",
                  background: styles.primaryColor,
                  border: "2px solid #fff",
                  boxShadow: `0 0 0 2px ${styles.primaryColor}30`
                }}
              />
              <Typography
                sx={{
                  fontSize: isMobile ? "11px" : "12px",
                  color: styles.modLabel
                }}
              >
                Daily Weight
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: isMobile ? 20 : 24,
                  height: 3,
                  borderRadius: 1,
                  background: styles.primaryColor
                }}
              />
              <Typography
                sx={{
                  fontSize: isMobile ? "11px" : "12px",
                  color: styles.modLabel
                }}
              >
                7-Day Rolling Avg
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {chartData.length > 0 && (
        <Box sx={{ textAlign: "center", px: 2, mb: 2 }}>
          <Typography
            sx={{
              fontSize: "11px",
              color: styles.modLabel,
              opacity: 0.7,
              mt: 0.5
            }}
          >
            {stats?.entries} entries in selected range
          </Typography>
        </Box>
      )}

      {/* Stats Cards */}
      {stats && (
        <Box
          sx={{
            background: styles.modBackgroundBlocks,
            borderRadius: 3,
            p: isMobile ? 2 : 3,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            mt: 2
          }}
        >
          <Box
            display="grid"
            gridTemplateColumns="repeat(3, 1fr)"
            gap={isMobile ? 1.5 : 2}
          >
            {[
              {
                label: "Average",
                value: stats.avg,
                color: styles.primaryColor
              },
              { label: "Lowest", value: stats.min, color: "#F6541C" },
              { label: "Highest", value: stats.max, color: styles.yellow }
            ].map((stat) => (
              <Paper
                key={stat.label}
                elevation={0}
                sx={{
                  p: isMobile ? 2 : 2.5,
                  background: "rgba(255,255,255,0.03)",
                  borderRadius: 3,
                  textAlign: "center"
                }}
              >
                <Typography
                  sx={{
                    fontSize: isMobile ? "10px" : "11px",
                    color: styles.modLabel,
                    letterSpacing: 1.1,
                    textTransform: "uppercase",
                    fontWeight: 600,
                    mb: 0.75
                  }}
                >
                  {stat.label}
                </Typography>

                <Typography
                  sx={{
                    fontSize: isMobile ? "22px" : "28px",
                    color: stat.color,
                    fontWeight: 800,
                    lineHeight: 1
                  }}
                >
                  {stat.value}
                </Typography>

                <Typography
                  sx={{
                    fontSize: "11px",
                    color: styles.modLabel,
                    mt: 0.4
                  }}
                >
                  {unit}
                </Typography>
              </Paper>
            ))}
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 0.75,
              borderRadius: 2,
              width: "fit-content",
              background: stats.isGain
                ? "rgba(234,179,8,0.12)"
                : "rgba(246,84,28,0.12)"
            }}
          >
            {stats.isGain ? (
              <TrendingUpIcon sx={{ color: styles.yellow, fontSize: 18 }} />
            ) : (
              <TrendingDownIcon sx={{ color: "#F6541C", fontSize: 18 }} />
            )}

            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 700,
                color: stats.isGain ? styles.yellow : "#F6541C"
              }}
            >
              {stats.change > 0 ? "+" : ""}
              {stats.change} {unit}
            </Typography>

            <Typography
              sx={{
                fontSize: "12px",
                color: styles.modLabel,
                fontWeight: 500
              }}
            >
              ({stats.changePercent > 0 ? "+" : ""}
              {stats.changePercent}%)
            </Typography>
          </Box>
        </Box>
      )}

      {/* Custom Date Range Dialog */}
      <Dialog
        open={openCustomDateDialog}
        onClose={() => setOpenCustomDateDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            background: styles.modBackgroundBlocks,
            borderRadius: 3,
            m: 2
          }
        }}
      >
        <DialogTitle sx={{ color: styles.color, fontWeight: 600 }}>
          Select Custom Date Range
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3} mt={1}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Start Date"
                value={dayjs.utc(customStartDate)}
                onChange={(newDate) =>
                  setCustomStartDate(newDate.format("YYYY-MM-DD"))
                }
                disableFuture
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: textFieldStyles
                  }
                }}
              />
              <DatePicker
                label="End Date"
                value={dayjs.utc(customEndDate)}
                onChange={(newDate) =>
                  setCustomEndDate(newDate.format("YYYY-MM-DD"))
                }
                disableFuture
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!dateError,
                    helperText: dateError,
                    sx: textFieldStyles
                  }
                }}
              />
            </LocalizationProvider>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setOpenCustomDateDialog(false);
              setDateError("");
            }}
            sx={{ textTransform: "none", color: styles.modLabel }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCustomDateApply}
            variant="contained"
            sx={{
              textTransform: "none",
              background: styles.primaryColor,
              px: 3,
              "&:hover": {
                background: styles.primaryColor,
                opacity: 0.9
              }
            }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{
            background: styles.modBackgroundBlocks,
            color: styles.color,
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(0,0,0,0.2)"
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WeightTrackerContent;
